import React, { useState, useRef } from 'react';
import { API_BASE_URL } from '../../services/api';
import { Upload, X, Image as ImageIcon } from 'lucide-react';

interface ImageUploadProps {
  taskId: number;
  onUploadSuccess: (file: any) => void;
  onError: (error: string) => void;
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
  taskId,
  onUploadSuccess,
  onError
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      onError('Please select an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB
      onError('File size must be less than 10MB');
      return;
    }

    await uploadFile(file);
  };

  const uploadFile = async (file: File) => {
    setIsUploading(true);

    try {
      const token = localStorage.getItem('token');

      // Step 1: Get presigned URL
      console.log('Getting presigned URL for file:', file.name, file.type);
      const urlResponse = await fetch(`${API_BASE_URL}/api/files/upload-url/${taskId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          filename: file.name,
          fileType: file.type
        })
      });

      if (!urlResponse.ok) {
        throw new Error('Failed to get upload URL');
      }

      const { uploadUrl, fileUrl, filename } = await urlResponse.json();
      console.log('Got presigned URL:', uploadUrl);

      // Step 2: Upload directly to S3
      console.log('Uploading to S3...');
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type
        },
        body: file
      });

      if (!uploadResponse.ok) {
        throw new Error('S3 upload failed');
      }

      console.log('S3 upload successful');

      // Step 3: Get image dimensions for metadata
      let imageWidth: number | null = null;
      let imageHeight: number | null = null;

      if (file.type.startsWith('image/')) {
        try {
          const img = new Image();
          await new Promise((resolve, reject) => {
            img.onload = () => {
              imageWidth = img.naturalWidth;
              imageHeight = img.naturalHeight;
              resolve(null);
            };
            img.onerror = reject;
            img.src = URL.createObjectURL(file);
          });
        } catch (err) {
          console.log('Could not get image dimensions:', err);
        }
      }

      // Step 4: Save metadata to database
      console.log('Saving metadata to database...');
      const metadataResponse = await fetch(`${API_BASE_URL}/api/files/upload-complete/${taskId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          filename,
          originalName: file.name,
          fileSize: file.size,
          fileType: file.type,
          fileUrl,
          imageWidth,
          imageHeight,
          description: ''
        })
      });

      if (!metadataResponse.ok) {
        throw new Error('Failed to save file metadata');
      }

      const result = await metadataResponse.json();
      console.log('Upload complete:', result);
      onUploadSuccess(result);
    } catch (error) {
      console.error('Upload error:', error);
      onError('Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  return (
    <div className="mb-4">
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragOver
            ? 'border-blue-400 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400'
        } ${isUploading ? 'opacity-50 pointer-events-none' : ''}`}
        onDrop={handleDrop}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileInputChange}
          className="hidden"
        />

        {isUploading ? (
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Uploading...</span>
          </div>
        ) : (
          <div className="cursor-pointer">
            <Upload className="mx-auto h-12 w-12 text-gray-400 mb-2" />
            <p className="text-gray-600">
              <span className="font-medium text-blue-600">Click to upload</span> or drag and drop
            </p>
            <p className="text-sm text-gray-500 mt-1">
              PNG, JPG, GIF up to 10MB
            </p>
          </div>
        )}
      </div>
    </div>
  );
};